import { ACTOR_UI, type StepDef, type StepPhase } from '../catalogos';

/**
 * Índice de los 15 pasos de la certificación, agrupados por fase.
 *
 * El proceso de la DGII es largo y se abandona a medias con facilidad, así que el índice muestra
 * siempre en qué fase se está y cuántos pasos faltan de cada una. El punto de color junto al
 * número indica quién tiene que actuar, para distinguir de un vistazo lo que depende de nosotros
 * de lo que está esperando a la DGII.
 */

interface StepperProps {
  steps: StepDef[];
  /** Paso en curso; todo lo anterior se considera hecho. */
  currentStep: number;
  onSelect: (id: number) => void;
}

export default function Stepper({ steps, currentStep, onSelect }: StepperProps) {
  // Se agrupa conservando el orden de STEP_DEFS: las fases van en secuencia, no alfabéticas.
  const phases: StepPhase[] = [];
  steps.forEach((s) => {
    if (!phases.includes(s.phase)) phases.push(s.phase);
  });

  return (
    <div className="space-y-5">
      {phases.map((phase) => {
        const items = steps.filter((s) => s.phase === phase);
        const hechos = items.filter((s) => s.id < currentStep).length;

        return (
          <div key={phase}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {phase}
              </p>
              <span className="text-xs text-gray-500">
                {hechos}/{items.length}
              </span>
            </div>

            <div className="space-y-0.5">
              {items.map((step, idx) => {
                const done = step.id < currentStep;
                const current = step.id === currentStep;
                const actor = ACTOR_UI[step.actor];
                const isLast = idx === items.length - 1;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => onSelect(step.id)}
                    className={`relative w-full flex items-start gap-3 text-left px-2 py-2 rounded-lg transition-colors ${
                      current ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Línea conectora entre pasos de la misma fase */}
                    {!isLast && (
                      <span
                        className={`absolute left-[23px] top-9 bottom-0 w-px ${
                          done ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                      ></span>
                    )}

                    <span
                      className={`relative z-10 w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${
                        done
                          ? 'bg-green-600 text-white'
                          : current
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-500'
                      }`}
                    >
                      {done ? <i className="ri-check-line"></i> : step.id}
                    </span>

                    <span className="min-w-0 flex-1 pt-0.5">
                      <span
                        className={`block text-sm leading-snug ${
                          current ? 'font-semibold text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {step.label}
                      </span>
                    </span>

                    <span
                      className={`w-1.5 h-1.5 rounded-full shrink-0 mt-3 ${actor.dot}`}
                      title={actor.titulo}
                    ></span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
